import {css} from '@emotion/react'
import styled from '@emotion/styled'
import {ThemeType} from 'types/Theme'

import {DatePickerVariant} from '@daedalus/core/src/datePicker/types'

const HEADER_HEIGHT = 58
const DAY_SIZE = 44

export const HORIZONTAL_PICKER_MIN_WIDTH = 600
export const HORIZONTAL_PICKER_MAX_WIDTH = 802

interface BaseProps {
  variant: DatePickerVariant
  isSingleMonth?: boolean
  singleDaySelected?: boolean
}

interface BasePropsWithTheme extends BaseProps {
  theme: ThemeType
}

const baseStyles = ({theme, variant, isSingleMonth}: BasePropsWithTheme) => {
  if (variant === 'vertical') {
    return css`
      position: relative;
      z-index: 0;
      width: 100%;
      height: 100%;
      padding: 0 ${theme.layout.spacing.s200}px;
      padding-bottom: ${theme.layout.spacing.s500}px;
    `
  }

  return css`
    position: relative;
    z-index: 0;
    width: ${isSingleMonth ? '100%' : `${HORIZONTAL_PICKER_MAX_WIDTH}px`};
    margin-bottom: ${theme.layout.spacing.s200}px;
  `
}

const customDayStyles = (
  theme: ThemeType,
  variant: DatePickerVariant,
  singleDaySelected?: boolean,
  isSingleMonth?: boolean
) => css`
  border: none;
  background-color: transparent;
  ${theme.typography.text.bodyS}
  display: table-cell;
  position: relative;
  text-align: center;
  cursor: pointer;
  vertical-align: middle;
  padding: 0;
  outline: none;
  border-radius: ${theme.layout.radius.lg}px;
  height: ${DAY_SIZE}px;
  width: 100%;
  color: ${theme.colors.content.neutral.c950};
  // force a fixed width day on horizontal picker as it has a max-width on the container
  ${variant === 'horizontal' &&
  css`
    width: ${DAY_SIZE}px;
  `}
  ${isSingleMonth &&
  css`
    &.day-selected {
      box-shadow: 0 0 0 1pt ${theme.colors.border.interactive.c500}; // use box-shadow instead of outline due to safari bug with rounded corners
    }
    &.day-disabled {
      .loading {
        visibility: hidden;
      }
    }
  `}

  &.disabled {
    cursor: default;
    color: ${theme.colors.content.neutral.c300};
    & .loading {
      display: none;
    }
  }

  &.weekends {
    background: ${theme.colors.background.neutral.c050};
  }

  &.outside {
    background: none;
  }

  &.selected {
    &:not(.outside) {
      background: ${theme.colors.background.interactive.c100};
      color: ${theme.colors.content.interactive.c500};
    }

    border-radius: 0;

    &:after,
    &:before {
      content: '';
      display: block;
      position: absolute;
      height: 100%;
      width: 4px;
      top: 0;
      background: ${theme.colors.background.interactive.c100};
    }

    &:before {
      left: -4px;
    }

    &:after {
      right: -4px;
    }

    &.outside {
      :before,
      :after {
        background: none;
      }
    }
  }

  &.startEnd {
    z-index: 2;
    padding: 0;

    .day-inner {
      background: ${theme.colors.background.interactive.c500};
      color: ${theme.colors.content.neutral.c000};
      border-radius: ${theme.layout.radius.lg}px;
      z-index: 1;
      position: relative;

      width: ${variant === 'vertical' ? '100%' : `${DAY_SIZE}px`};
      height: ${DAY_SIZE}px;
      line-height: ${DAY_SIZE}px;

      &:hover {
        background: ${theme.colors.background.interactive.c500};
      }

      &:after {
        content: '';
        display: block;
        position: absolute;
        width: 0;
        top: 7px;
        z-index: 1;
        background: transparent;
        border-top: 15px solid transparent;
        border-bottom: 15px solid transparent;
      }
    }

    &.start {
      ${singleDaySelected
        ? css`
            border-radius: ${theme.layout.radius.lg}px;
          `
        : css`
            border-radius: ${theme.layout.radius.lg}px 0 0
              ${theme.layout.radius.lg}px;
          `}

      &:before {
        display: none;
      }

      & .day-inner {
        &:after {
          right: -4px;
          border-left: 4px solid ${theme.colors.background.interactive.c500};
        }
      }
    }

    &.end {
      border-radius: 0 ${theme.layout.radius.lg}px ${theme.layout.radius.lg}px 0;
      &:after {
        display: none;
      }

      & .day-inner {
        &:after {
          left: -4px;
          border-right: 4px solid ${theme.colors.background.interactive.c500};
        }
        & .price,
        .loading {
          display: none;
        }
      }
    }

    ${singleDaySelected &&
    css`
      &:after {
        display: none;
      }
    `}
  }
`

const monthsWrapper = (
  theme: ThemeType,
  variant: DatePickerVariant,
  isSingleMonth?: boolean
) => {
  if (variant === 'vertical') {
    return css`
      display: table;
      user-select: none;
      width: 100%;
      border-spacing: ${theme.layout.spacing.s300}px;
    `
  }

  if (isSingleMonth) return ''

  return css`
    display: flex;
    flex: 1;
    &:before {
      content: '';
      width: 1px;
      top: ${HEADER_HEIGHT}px;
      height: calc(100% - ${HEADER_HEIGHT}px);
      background-color: ${theme.colors.border.neutral.c100};
      position: absolute;
      left: 50%;
    }
  `
}

const captionWrapper = (
  theme: ThemeType,
  variant: DatePickerVariant,
  isSingleMonth?: boolean
) => {
  if (variant === 'vertical') {
    return css``
  }
  return css`
    position: relative;
    border-bottom: ${isSingleMonth ? 0 : 1}px solid;
    border-color: ${theme.colors.border.neutral.c100};
  `
}
const customNavButtonStyles = (
  theme: ThemeType,
  variant: DatePickerVariant,
  position: 'right' | 'left' | 'none',
  isSingleMonth?: boolean
) => {
  switch (position) {
    case 'right':
      return css`
        right: 0;
      `
    case 'left':
      return css`
        left: 0;
      `

    default:
      if (variant === 'vertical') {
        return css``
      }
      return css`
        position: absolute;
        top: 0;
        margin: 0px;
        border: none;
        box-shadow: none;
        outline: none;
        font: inherit;
        background: transparent;
        padding: 0px
          ${isSingleMonth
            ? theme.layout.spacing.s600
            : theme.layout.spacing.s700}px;
        height: ${HEADER_HEIGHT}px;
        cursor: pointer;
        color: ${theme.colors.content.interactive.c500};
        &:disabled {
          color: ${theme.colors.content.neutral.c600};
          cursor: not-allowed;
        }
        /* @mirror */
      `
  }
}

const calendarWrapper = (
  theme: ThemeType,
  variant: DatePickerVariant,
  isSingleMonth?: boolean
) => {
  if (variant === 'vertical') {
    return css`
      width: 100%;
      border-spacing: ${theme.layout.spacing.s200}px;
      table-layout: fixed;
    `
  }
  if (isSingleMonth) {
    return css`
      padding: 0;
      border-spacing: ${theme.layout.spacing.s200}px;
      width: 100%;
      table-layout: fixed;
    `
  }
  return css`
    padding: 0 17px;
    border-spacing: ${theme.layout.spacing.s200}px;
    width: 100%;
    table-layout: fixed;
  `
}

export const DatePickerElement = styled.div<BaseProps>(
  ({theme, variant, isSingleMonth = false, singleDaySelected = false}) => css`
    ${baseStyles({theme, variant, isSingleMonth})}
    .wrapper {
      height: 100%;
    }
    .day-cell {
      ${customDayStyles(theme, variant, singleDaySelected, isSingleMonth)}
    }
    .months-wrapper {
      ${monthsWrapper(theme, variant, isSingleMonth)}
    }
    .month-wrapper {
      flex: 1 1 0%;
    }
    .caption-wrapper {
      ${captionWrapper(theme, variant, isSingleMonth)}
    }
    .nav-button {
      ${customNavButtonStyles(theme, variant, 'none', isSingleMonth)}
    }
    .nav-button-next {
      ${customNavButtonStyles(theme, variant, 'right')}
    }
    .nav-button-previous {
      ${customNavButtonStyles(theme, variant, 'left')}
    }
    .calendar-week-days-wrapper {
      ${(variant === 'vertical' || isSingleMonth) &&
      css`
        display: none;
      `}
    }
    .calendar-wrapper {
      ${calendarWrapper(theme, variant, isSingleMonth)}
    }
  `
)

export const CaptionElement = styled.div<{
  datePickerVariant: DatePickerVariant
}>(({theme, datePickerVariant}) => {
  if (datePickerVariant === 'vertical') {
    return css`
      ${theme.typography.text.titleM};
      text-align: center;
      color: ${theme.colors.content.neutral.c950};
      padding: ${theme.layout.spacing.s500}px ${theme.layout.spacing.s300}px
        ${theme.layout.spacing.s200}px;
    `
  }

  return css`
    ${theme.typography.text.titleM};
    text-align: center;
    color: ${theme.colors.content.neutral.c950};
    display: flex;
    justify-content: center;
    align-items: center;
    height: ${HEADER_HEIGHT}px;
  `
})

export const WeekDayContainer = styled.div<{
  variant: DatePickerVariant
  isWeekend?: boolean
}>(({theme, variant, isWeekend = false}) => {
  const shared = css`
    ${theme.typography.text.bodyS};
    color: ${theme.colors.content.neutral.c600};
    text-align: center;

    & abbr[title] {
      border-bottom: none;
      text-decoration: none;
    }
  `

  if (variant === 'vertical') {
    return css`
      ${shared};
      display: table-cell;
      text-align: center;
    `
  }

  return css`
    ${shared};
    width: ${DAY_SIZE}px;
    margin: ${theme.layout.spacing.s500}px 0;
    ${isWeekend &&
    css`
      background-color: ${theme.colors.background.neutral.c100};
      border-radius: ${theme.layout.radius.sm}px;
    `}
  `
})
